import React from 'react';
import {Control, useWatch} from 'react-hook-form';
import {Market} from 'types';
import {MarketNamingTemplateDescriptions} from './MarketNamingTemplateDescriptions';
import {useMarketNamingTemplateValidation} from './hooks';
import {SeparatorCompat as Separator} from 'components/FluentUICompat';
import {ControlledToggle} from 'components/hookForms';

type MarketProps = keyof Market;

export type MarketNamingTemplateKeys = Extract<
	MarketProps,
	'regulationNamingTemplates' | 'regulatoryDocumentNamingTemplates'
>;

export type InputMarketProps = Pick<
	Market,
	| 'id'
	| 'name'
	| 'regulationNamingTemplates'
	| 'regulatoryDocumentNamingTemplates'
>;

type MarketNamingTemplateFieldChildProps = {
	validateFieldValue: (value: string) => boolean | string;
	isMarketSelected: boolean;
	placeholder: string;
};

type MarketNamingTemplateFieldProps = {
	markets: InputMarketProps[];
	namingHint: string;
	templatesToUse: MarketNamingTemplateKeys;
	label: string;
	control: Control<any>;
	children: (props: MarketNamingTemplateFieldChildProps) => React.ReactNode;
};

export const MarketNamingTemplateField: React.FC<
	MarketNamingTemplateFieldProps
> = ({markets, namingHint, templatesToUse, label, control, children}) => {
	const {validateFieldValue, isMarketSelected, placeholder} =
		useMarketNamingTemplateValidation(markets, namingHint, templatesToUse);
	// The free-text mode is a persisted field (`isFreeTextName`) rather than inferred from the entity name.
	const isFreeTextName = useWatch({control, name: 'isFreeTextName'});

	const validateFieldValueFinal = (value: string) =>
		Boolean(isFreeTextName) || validateFieldValue(value);

	return (
		<React.Fragment>
			{children({
				validateFieldValue: validateFieldValueFinal,
				isMarketSelected,
				placeholder,
			})}
			<MarketNamingTemplateDescriptions
				markets={markets}
				templatesToUse={templatesToUse}
			/>
			{markets.length > 0 && (
				<>
					<ControlledToggle
						control={control}
						name='isFreeTextName'
						label={label}
					/>
					<Separator />
				</>
			)}
		</React.Fragment>
	);
};
